// src/services/socketApi.ts
import { IChatListItem } from 'src/components/pages/messenger/components/list/item/ChatListItem'
import { IMessage } from 'src/components/pages/messenger/components/window/list/item/Message'
import { socket } from 'src/socket'
import { api } from './api'

export enum ChatEvent {
  SendMessage = 'sendMessage',
  ReceiveMessage = 'receiveMessage',
  MessageSent = 'messageSent',
  RequestAllMessages = 'requestAllMessages',
  ReceiveAllMessages = 'receiveAllMessages',
  NewChatCreated = 'newChatCreated',
  ChatUpdated = 'chatUpdated',
  GetChats = 'getChats',
  JoinChat = 'joinChat',
  LeaveChat = 'leaveChat',
  ReadChat = 'readChat',
  ChatRead = 'chatRead',
  JoinAuction = 'joinAuction',
  LeaveAuction = 'leaveAuction',
  AuctionBid = 'auctionBid',
  AuctionOutbid = 'auctionOutbid',
}

interface PageInfo {
  hasNextPage: boolean
  cursor: string
}
interface BiderectionalPageInfo {
  hasNextPage: boolean
  hasPreviousPage: boolean
  nextPageCursor: string
  prevPageCursor: string
}

interface sendMessagePayload {
  content: string
  recipientId?: string
  chatId?: string
  attachments?: string[]
}

export const socketApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUnreadMessages: builder.query({
      queryFn: () => {
        return { data: 0 }
      },
    }),
    getChats: builder.infiniteQuery<
      { chats: IChatListItem[]; pageInfo: PageInfo },
      { limit: number; cursor?: string },
      { limit?: number; cursor?: string }
    >({
      query: ({ pageParam, queryArg }) => {
        return {
          url: '/chat',
          params: { ...queryArg, ...pageParam },
        }
      },
      providesTags: ['Chats'], // Add cache tag
      infiniteQueryOptions: {
        initialPageParam: {},
        getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams, args) => {
          if (!lastPage.pageInfo.hasNextPage) {
            return undefined
          }

          return { limit: args.limit, cursor: lastPage.pageInfo.cursor }
        },
      },
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        // Handlers are kept in named consts so the exact ones can be removed on
        // teardown. socket.off(event) with no handler removes ALL listeners for
        // that event — which would also kill the other components' handlers.
        const onNewChatCreated = (data: IChatListItem) => {
          updateCachedData((draft) => {
            draft.pages[0].chats.unshift(data)
          })
        }

        const onReceiveMessage = (message: IMessage, isRecipientInChat: boolean) => {
          updateCachedData((draft) => {
            const chats = draft.pages.map((page) => page.chats).flat()
            const chatToUpdate = chats.find((chat: IChatListItem) => chat.id === message.chatId)
            if (chatToUpdate) {
              Object.assign(chatToUpdate, {
                message: message.content,
                time: message.time,
                unreadCount:
                  chatToUpdate.senderId !== message.senderId && !isRecipientInChat
                    ? chatToUpdate.unreadCount + 1
                    : chatToUpdate.unreadCount,
              })
            }
          })
        }

        socket.on(ChatEvent.NewChatCreated, onNewChatCreated)
        socket.on(ChatEvent.ReceiveMessage, onReceiveMessage)

        try {
          await cacheDataLoaded
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
        socket.off(ChatEvent.NewChatCreated, onNewChatCreated)
        socket.off(ChatEvent.ReceiveMessage, onReceiveMessage)
      },
    }),
    getChatMessages: builder.infiniteQuery<
      {
        messages: IMessage[]
        pageInfo: BiderectionalPageInfo
        // Read boundaries for ticks + unread divider (read receipts).
        readState?: { myLastReadAt: string | null; otherLastReadAt: string | null }
      },
      { limit: number; chatId: string; messageId?: string; skip?: number },
      { cursor?: string; limit?: number }
    >({
      query: ({ pageParam, queryArg }) => {
        return {
          url: `/chat/${queryArg.chatId}/messages`,
          params: {
            ...queryArg,
            ...pageParam,
            cursor: pageParam.cursor ? pageParam.cursor : queryArg.messageId,
            skip: pageParam.cursor ? 1 : 0,
          },
        }
      },
      infiniteQueryOptions: {
        initialPageParam: { limit: 20 },
        // более новые
        getPreviousPageParam: (firstPage, allPages, firstPageParam, allPageParams, args) => {
          if (!firstPage.pageInfo.hasPreviousPage) {
            return undefined
          }

          return { cursor: firstPage.pageInfo.prevPageCursor, limit: -args.limit }
        },
        // более старые
        getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams, args) => {
          if (!lastPage.pageInfo.hasNextPage) {
            return undefined
          }

          return { cursor: lastPage.pageInfo.nextPageCursor, limit: args.limit }
        },
      },
      async onCacheEntryAdded(
        arg,
        { updateCachedData, dispatch, cacheDataLoaded, cacheEntryRemoved },
      ) {
        socket.emit(ChatEvent.JoinChat, { chatId: arg.chatId })

        // Named handlers so teardown removes exactly these, not every
        // ReceiveMessage/ReadChat listener registered elsewhere.
        const onReceiveMessage = (message: IMessage) => {
          if (message.chatId !== arg.chatId) return
          updateCachedData((draft) => {
            draft.pages[0].messages.unshift(message)
          })
        }
        const onReadChat = (data: { chatId: string }) => {
          if (data.chatId !== arg.chatId) return
          dispatch(
            socketApi.util.updateQueryData('getChats', { limit: 20 }, (draft) => {
              const chats = draft.pages.map((page) => page.chats).flat()
              const chatToUpdate = chats.find((chat: IChatListItem) => chat.id === data.chatId)
              if (chatToUpdate) {
                Object.assign(chatToUpdate, { unreadCount: 0 })
              }
            }),
          )
        }

        socket.on(ChatEvent.ReceiveMessage, onReceiveMessage)
        socket.on(ChatEvent.ReadChat, onReadChat)

        try {
          await cacheDataLoaded
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved
        socket.emit(ChatEvent.LeaveChat, { chatId: arg.chatId })
        socket.off(ChatEvent.ReceiveMessage, onReceiveMessage)
        socket.off(ChatEvent.ReadChat, onReadChat)
      },
    }),
    sendMessage: builder.mutation<IMessage, sendMessagePayload>({
      // Resolves on the server ack. Previously it had no timeout, so a dropped
      // socket left the mutation pending forever and the send failed silently;
      // it now rejects after 10s so the UI can surface a retry.
      queryFn: (args) =>
        new Promise((resolve) => {
          let settled = false
          const timer = setTimeout(() => {
            if (settled) return
            settled = true
            resolve({ error: { status: 'TIMEOUT_ERROR', error: 'Message send timed out' } })
          }, 10000)
          socket.emit(ChatEvent.SendMessage, args, (ack: IMessage | { error?: string } | null) => {
            if (settled) return
            settled = true
            clearTimeout(timer)
            // A refused message is acknowledged with { error }. It used to be
            // taken for the sent message: the input was cleared and the text lost.
            if (!ack || !('id' in ack) || !ack.id) {
              resolve({
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'MESSAGE_NOT_SENT',
                  data: { code: 'MESSAGE_NOT_SENT' },
                },
              })
              return
            }
            resolve({ data: ack })
          })
        }),
    }),
  }),
})

// Підключаємо функції для використання в компоненті
export const { useSendMessageMutation, useGetChatsInfiniteQuery, useGetChatMessagesInfiniteQuery } =
  socketApi
